import React from "react"
import "./app.css"
import LoginPage from "../components/login/LoginPage"
import PhoneVerify from "../components/login/PhoneVerify"
import VerifyCode from "../components/login/VerifyCode"
import PinReset from "../components/login/PinReset"
import ValidatePhone from "../components/login/ValidatePhone"
import Header from "../components/layout/Header"
import FailedRequestsBanner from "../components/layout/FailedRequestsBanner"
import MainMenu from "../components/layout/MainMenu"
import { BrowserRouter as Router, Route, Switch } from "react-router-dom"
import Groups from "../components/groups/Groups"
import SignUpPage from "../components/login/SignUpPage"
import { ProtectedRoute } from "../components/login/protected.route"
import ReactGA from "react-ga4"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { library } from "@fortawesome/fontawesome-svg-core"
import CustomAlert from "../components/layout/CustomAlert"
import MaintenancePage from "../components/layout/MaintenancePage"
import { locales } from "../i18n"
import { useLingui } from "@lingui/react"

import {
  faPeopleCarry,
  faToolbox,
  faHandHoldingUsd,
  faCoins,
  faUser,
  faUsers,
  faSignOutAlt,
  faAngleRight,
  faTimes,
  faExclamationCircle,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons"
import { useDispatch, useSelector } from "react-redux"
import { manifestURLs, navigationMap, getUserIPData } from "./shared/helpers"
import { setCurrentPage } from "../store/reducers/navigation"
import { loadLocalData } from "../store/actions/getVslas"
import { Trans } from "@lingui/macro"
import { precacheData } from "../store/actions/precache"
import { processOfflineQueue, resetQueue } from "../store/api/queue"

library.add(faPeopleCarry)
library.add(faToolbox)
library.add(faHandHoldingUsd)
library.add(faCoins)
library.add(faUser)
library.add(faSignOutAlt)
library.add(faUsers)
library.add(faAngleRight)
library.add(faTimes)
library.add(faExclamationCircle)
library.add(faArrowLeft)

ReactGA.initialize([
  {
    trackingId: window.REACT_APP_PWA_APP_TRACKING_ID,
    legacyDimensionMetric: false,
    gaOptions: {
      send_page_view: true,
    },
  },
])

const isAndroid = navigator.userAgent.toLowerCase().indexOf("android") > -1

const App = () => {
  const dispatch = useDispatch()
  const mounted = React.useRef(false)

  // Maintenance mode control - controlled via public/config.js
  const isMaintenanceMode = window.MAINTENANCE_MODE || false

  // Check if language is selected
  const isLanguageSelected = Object.keys(locales).includes(
    localStorage.getItem("lang"),
  )

  // Only show maintenance if language is selected AND maintenance is enabled
  const shouldShowMaintenance = isMaintenanceMode && isLanguageSelected
  const { currentPage, uploadStatus, queue, user, isConnected } = useSelector(
    ({
      vsla,
      currentPage,
      isConnected,
      uploadStatus,
      queue,
      currentOngoingMeeting,
      user,
      vlsasLoadingStarted,
    }) => ({
      vsla,
      isConnected,
      currentPage,
      uploadStatus,
      queue,
      currentOngoingMeeting,
      user,
      vlsasLoadingStarted,
    }),
  )

  const { i18n } = useLingui()

  React.useEffect(() => {
    if (!isConnected) {
      const el = document.getElementsByTagName("body")[0].style
      el.overscrollBehavior = "contain"
      return
    }
  }, [isConnected])

  React.useEffect(() => {
    dispatch(precacheData())
    const lang = i18n.locale
    if (lang) {
      dispatch({ type: "SET_LANGUAGE", payload: lang })
    }
  }, [])

  const handleNavigation = React.useCallback(() => {
    const metadata = currentPage.split("-")
    const pages = navigationMap[metadata[0]]
    const currentPageDef = pages[metadata[1]]
    if (!currentPageDef.backPage) {
      window.history.push("/")
    }
    return dispatch(setCurrentPage(`${metadata[0]}-${currentPageDef.backPage}`))
  }, [currentPage, dispatch])

  const handleBackButton = React.useCallback(() => {
    if (!currentPage) {
      return
    }
    window.history.pushState({}, "")
    handleNavigation()
  }, [currentPage, handleNavigation])

  const setupWindowHistoryState = React.useCallback(() => {
    window.addEventListener("popstate", handleBackButton)
  }, [handleBackButton])

  React.useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/group",
    })
    mounted.current = true
    if (isConnected) {
      getUserIPData()
    }

    document
      .querySelector("#manifest-placeholder")
      .setAttribute("href", manifestURLs[window.REACT_APP_PWA_APP_ENV])
    if (i18n.locale === "ar") {
      document.querySelector("body").setAttribute("dir", "rtl")
    } else {
      document.querySelector("body").setAttribute("dir", "ltr")
    }
    if (isAndroid) {
      setupWindowHistoryState()
    }
    return () => {
      if (isAndroid) {
        window.removeEventListener("popstate", handleBackButton)
      }
    }
  }, [i18n.locale, handleBackButton, setupWindowHistoryState, isConnected])

  const [errorIsVisible, setErrorVisible] = React.useState(false)
  const [successIsVisible, setSuccessVisible] = React.useState(false)
  const onDismiss = () => {
    setSuccessVisible(false)
    setErrorVisible(false)
  }

  React.useEffect(() => {
    if (
      !isConnected &&
      [
        // "group-repayLoan",
        "group-writeOffLoan",
        // "group-createLoan",
        // "group-sellShares",
        // "group-addWelfare",
        // "group-requestWelfare",
        // "group-meetingTransactions",
      ].includes(currentPage)
    ) {
      dispatch(setCurrentPage("group-memberList"))
    }
  }, [isConnected])

  // Effect to rehydrate local data from IndexedDB on user login
  React.useEffect(() => {
    if (user && user.id) {
      // Once the user is logged in, load all their local data.
      setTimeout(() => {
        dispatch(loadLocalData())
        resetQueue()
        processOfflineQueue()
      }, 1000)
    }
  }, [user, dispatch])

  return (
    <>
      <MaintenancePage isVisible={shouldShowMaintenance} />
      {!shouldShowMaintenance && (
        <Router basename="">
          <Header />
          <div className="main-content">
            <Switch>
              <FailedRequestsBanner />
            </Switch>
            <ToastContainer
              position="top-left"
              autoClose={3000}
              hideProgressBar
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnVisibilityChange
              draggable
              pauseOnHover
            />

            {isLanguageSelected ? (
              <ProtectedRoute path="/" exact strict component={LoginPage} />
            ) : (
              <ProtectedRoute path="/" exact strict component={MainMenu} />
            )}
            <Route path="/SignUpPage" exact strict component={SignUpPage} />

            <Route path="/ValidatePhone" exact component={ValidatePhone} />

            <Route path="/PhoneVerify" exact component={PhoneVerify} />

            <Route path="/VerifyCode" exact component={VerifyCode} />

            <Route path="/PinReset" exact component={PinReset} />
            <ProtectedRoute path="/Groups" exact strict component={Groups} />
          </div>
          {/* <CustomAlert
            isOpen={errorIsVisible}
            toggle={onDismiss}
            style={{
              position: "absolute",
              top: 12,
              width: "90%",
              zIndex: 1,
              margin: "0 5%",
              background: "#e71919",
              color: "#fff",
            }}
            color="success"
          >
            <h4 className="alert-heading">vsla.online</h4>
            <hr />
            <p className="mb-0">
              <Trans id="offline.upload.error" />
            </p>
          </CustomAlert> */}
          <CustomAlert
            isOpen={successIsVisible}
            toggle={onDismiss}
            style={{
              position: "absolute",
              top: 12,
              width: "90%",
              zIndex: 1,
              margin: "0 5%",
              background: "#1bc47d",
              color: "#fff",
            }}
            color="success"
          >
            <h4 className="alert-heading">vsla.online</h4>
            <hr />
            <p className="mb-0">
              <Trans id="offline.upload.success" />
            </p>
          </CustomAlert>
        </Router>
      )}
    </>
  )
}

export default App
